import { useCallback, useEffect, useState, type ReactNode } from "react";

import type {
  AddressView,
  RiderApi,
  StopPlanStop,
  StopPlanView,
  TravelMode,
} from "../rider-api.ts";
import { SCOPES } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import {
  ConflictCard,
  EmptyRow,
  Group,
  MarketShell,
  type BadgeValue,
  type RowValue,
} from "./MarketShell.tsx";
import { Absent } from "./PhoneShell.tsx";
import { MarketState } from "./TrancheState.tsx";

/**
 * `Multi-stop in trip` — a stop plan in progress, and the two-hop read that finally names a stop.
 *
 * | Drawn                                     | From                                                       |
 * | ----------------------------------------- | ---------------------------------------------------------- |
 * | `In trip · 3 stops`                       | `plan.stops.length`                                        |
 * | `CityCab · XL`                            | the tenant's own name; the class is **nothing**, below     |
 * | `stop 1 of 2 done`                        | `plan.stops.status`, over the stops before the drop-off    |
 * | `Heading to Upper Hill`                   | `plan.stops.addressId` → GEO-02 `address.formattedAddress` |
 * | `Stop 2 of 2, then JKIA`                  | the same two, over `plan.stops.sequence`                   |
 * | `arrive about 13:40`                      | **nothing** — an estimate over stops needs their dwell     |
 * | `Kilimani, Yaya Centre` and the two rows  | the same address read, once per distinct `addressId`       |
 * | `Arrived 12:58 · left 13:02`              | `plan.stops.arrivedAt` and `plan.stops.completedAt`        |
 * | `About 9 min`, `About 40 min`             | GEO-04 `result.durationSeconds`, one leg each              |
 * | `completed`, `next`, `later`              | `plan.stops.status` and `plan.stops.sequence`              |
 * | the red `SOS`                             | SAF-01's `open_sos`, on `plan.tripId`                      |
 * | `Share trip`                              | SAF-01's `set_sharing`, on the same                        |
 *
 * ## The hop, and why this screen exists at all
 *
 * This was the last rider screen the measurement graded **blocked**, on one element:
 * `Heading to Upper Hill`, a stop's place as a name. A stop carried a 1-to-200-character
 * `locationReference` whose own fixtures were `place:a` and `addr-1`, and
 * [ADR 0035](../../../docs/adr/0035-an-address-is-a-record-geo-02-owns-and-re-resolves.md)'s address
 * read could not answer a screen holding one of those.
 * [ADR 0036](../../../docs/adr/0036-a-stop-names-an-address-and-trp-04-confirms-it.md) narrowed the
 * field to a UUID naming a GEO-02 address and made the create command confirm every one of them
 * before a plan is written. So the chain this component makes is two calls, not one:
 *
 *     GET /v1/tenants/{tenantId}/stop-plans/{planId}  →  plan.stops[].addressId   trip:stops:read
 *     GET /v1/addresses/{addressId}                   →  address.formattedAddress address:read
 *
 * **Both hops, and the second is the half that failed before.** A `read` need in the measurement
 * says the operation exists and takes an identifier the screen holds; it cannot say where the screen
 * got the identifier. `tests/contract/mobile-screen-support.test.ts` names the hop against the
 * contracts and `tests/integration/stop-address-path.test.ts` assembles it over real sockets and
 * reads `Upper Hill, Nairobi` back out of GEO-02. This file is the third place that chain appears
 * and the first one a rider sees.
 *
 * **Nothing on this platform creates a stop plan.** `postTenantsStopPlans` still has no caller in
 * any service or application. That is a fact about the writer rather than about the read: the plan
 * this screen reads is the same plan whose `status` and `sequence` the measurement already counts as
 * met, and an argument that the place is unreadable because nobody writes a plan proves every other
 * element of this screen unreadable too. R-47 settles that at length. An earlier tranche published a
 * stop-address **field** and reverted it, which was right for a different reason: a field nothing
 * confirms is ADR 0035's own complaint about `pickupAddressId` — "a UUID a client supplies with no
 * referent" — one service over. The address a stop names here is one the service that owns the
 * command has already resolved against GEO-02.
 *
 * ## One request per distinct address, and the de-duplication is here because only here knows
 *
 * ADR 0036 section 2 confirms **distinct** addresses rather than stops: two stops at one doorway are
 * one question, and a twenty-stop plan must not be a twenty-fold burst on a provider-backed service
 * for one command. Drawing a plan is the same shape, so the places are fetched once per distinct
 * `addressId`, in sequence — and a stop whose address could not be read is drawn as the absence
 * rather than as a plausible place name.
 *
 * ## Three elements of the design have no source, and are drawn as the absence
 *
 *   - **`· XL` — the vehicle class.** The plan publishes `tripId`, `tripVersion`, `riderId`,
 *     `driverId`, `version` and its stops. No product, no class, no vehicle. So the app bar's
 *     sub-line carries the tenant and then the absence.
 *   - **`arrive about 13:40`.** An arrival over the remaining stops is the legs plus the dwell
 *     planned at each one, and nothing publishes `plan.stops.waitSeconds` or
 *     `plan.stops.dwellSeconds` — the absence the measurement already records against
 *     `rider · Multi-stop`'s own `wait 5 min`. Summing the legs alone would draw a time a rider acts
 *     on and arrives after.
 *   - **A leg past its own `freshUntil`.** GEO-04 publishes when its answer stops being fresh, and
 *     this screen does not poll: nothing delivers a trip's progress unasked to a rider application
 *     (RTM-001), and a one-second poll would make a static screen look live, which is the judgement
 *     `Order tracking` records for the same reason. So a duration stands while GEO-04 says it is
 *     fresh and becomes the absence afterwards — reached by advancing the clock rather than by
 *     rendering a variant.
 */

export interface MultiStopInTripProps {
  readonly api: RiderApi;
  readonly scopes: ReadonlySet<string>;
  readonly tenantId: string;
  /** The plan. Not discoverable: TRP-04 publishes a read by `planId` and no listing. */
  readonly planId: string | undefined;
  readonly onBack: () => void;
  /** SAF-01's `messageId`, which is the field it de-duplicates on rather than a key. */
  readonly newId: () => string;
  readonly now: () => number;
}

/**
 * How many legs this screen will ask GEO-04 about.
 *
 * Twelve, which is `Products · stock`'s `STOCK_LOOKUPS` for the same shape of sweep: one request per
 * row against a service addressed by one subject at a time. A thirteenth stop still to come is drawn
 * with its duration never asked for, said in its own sub-line rather than left blank.
 */
const LEG_LOOKUPS = 12;

/**
 * The travel mode GEO-04 requires, which nothing on this plan publishes.
 *
 * `car`, and it is this build's value rather than a read one — the same shape of decision
 * [Q-126](../../../docs/design/DESIGN_QUESTIONS.md) records for `driver · 4 To pickup`'s
 * `motorcycle`. GEO-04 requires `mode` with four published values; the plan carries no product,
 * class or vehicle, so omitting it is impossible and deriving it would be this application inventing
 * a mapping. `car` is the design's own sample for this screen, whose app bar reads `CityCab · XL` —
 * a class drawn in the same bundle that draws `Standard`, `XL` and `Assist` as cars. It is the
 * approved sample rather than a guess, and it is wrong for a trip taken on a motorcycle.
 */
const STOP_TRAVEL_MODE: TravelMode = "car";

/** The tenant the design's own sample is, which is the only name this screen has for one. */
const TENANT_NAME = "CityCab";

/** Why neither safety control can be pressed, when the sign-in does not carry SAF-01's scope. */
const NO_SAFETY_SCOPE = "Your sign-in doesn't allow this. It needs safety:write.";

/** What GEO-04 answered for one leg, or why it could not be asked. */
type Leg =
  | { readonly kind: "duration"; readonly seconds: number; readonly freshUntil: number }
  | { readonly kind: "unavailable"; readonly because: string };

export function MultiStopInTrip(props: MultiStopInTripProps): ReactNode {
  const { api, tenantId, planId, now } = props;

  const [plan, setPlan] = useState<StopPlanView | undefined>(undefined);
  const [places, setPlaces] = useState<ReadonlyMap<string, AddressView>>(new Map());
  const [placeFailures, setPlaceFailures] = useState<ReadonlyMap<string, string>>(new Map());
  const [placesRead, setPlacesRead] = useState(false);
  const [legs, setLegs] = useState<ReadonlyMap<string, Leg>>(new Map());
  const [legsRead, setLegsRead] = useState(false);
  const [state, setState] = useState<ViewState>({ kind: "loading" });
  /** A safety command SAF-01 refused, drawn in the design file's own conflict card. */
  const [refused, setRefused] = useState<Refusal | undefined>(undefined);
  const [tick, setTick] = useState(() => now());

  const load = useCallback(() => {
    if (planId === undefined) return;
    setState({ kind: "loading" });
    setPlacesRead(false);
    setLegsRead(false);
    api
      .stopPlan({ tenantId, planId })
      .then((answer) => {
        setPlan(answer);
        setState({ kind: "ready" });
      })
      .catch((error: unknown) => setState(stateForError(error, [SCOPES.stopPlan])));
  }, [api, tenantId, planId]);

  useEffect(load, [load]);

  /*
   * The second hop: one read per distinct address, in sequence.
   *
   * Sequential rather than `Promise.all`, which is ADR 0036 section 2's own bound applied to drawing
   * a plan rather than to writing one. A failure is recorded per address instead of failing the
   * screen: a plan of three stops whose second address does not resolve is a screen that can name
   * two places and must not name the third.
   */
  useEffect(() => {
    const stops = plan?.stops;
    if (stops === undefined) return;
    const distinct = [...new Set(stops.map((stop) => stop.addressId))];
    let current = true;
    void (async (): Promise<void> => {
      const found = new Map<string, AddressView>();
      const failed = new Map<string, string>();
      for (const addressId of distinct) {
        try {
          found.set(addressId, await api.address(addressId));
        } catch {
          /*
           * Why the words are this screen's rather than the service's.
           *
           * GEO-02 answers 404 for an address in another tenant exactly as for one that never
           * existed, which ADR 0036 records as deliberate — which of the two it was is a fact about
           * somebody else's tenant. So this says what this screen does not know rather than
           * repeating an answer that is the same for two different causes.
           */
          failed.set(addressId, "No place name: GEO-02 did not answer for this stop's address");
        }
        if (!current) return;
      }
      setPlaces(found);
      setPlaceFailures(failed);
      setPlacesRead(true);
    })();
    return (): void => {
      current = false;
    };
  }, [api, plan]);

  /*
   * The legs, one GEO-04 read each, bounded at `LEG_LOOKUPS`.
   *
   * A leg runs from where the trip was last known to be to the stop it is drawn against: the last
   * **completed** stop for the first one still to come, and the stop before it for each one after
   * that. Nothing on this plan publishes where the vehicle is, so the leg into the next stop is
   * measured from the stop it left rather than from the car — which overstates it by however far the
   * car has already come, and is recorded as a design question rather than smoothed over.
   */
  useEffect(() => {
    const stops = plan?.stops;
    if (stops === undefined || !placesRead) return;
    const ordered = inSequence(stops);
    const ahead = ordered.filter(stillToCome);
    let current = true;
    void (async (): Promise<void> => {
      const found = new Map<string, Leg>();
      for (const [index, stop] of ahead.entries()) {
        const from = index === 0 ? lastCompleted(ordered, stop) : ahead[index - 1];
        const origin = from === undefined ? undefined : places.get(from.addressId);
        const destination = places.get(stop.addressId);
        if (index >= LEG_LOOKUPS) {
          found.set(stop.stopId, {
            kind: "unavailable",
            because: `No time to this stop: this screen asks GEO-04 about ${String(LEG_LOOKUPS)} legs and this plan has more`,
          });
          continue;
        }
        if (origin === undefined || destination === undefined) {
          found.set(stop.stopId, {
            kind: "unavailable",
            because:
              from === undefined
                ? "No time to this stop: nothing published where this trip has reached"
                : "No time to this stop: one of its two addresses did not resolve",
          });
          continue;
        }
        try {
          const answer = await api.distanceEta({
            origin: { latitude: origin.latitude, longitude: origin.longitude },
            destination: { latitude: destination.latitude, longitude: destination.longitude },
            mode: STOP_TRAVEL_MODE,
          });
          found.set(stop.stopId, {
            kind: "duration",
            seconds: answer.result.durationSeconds,
            freshUntil: Date.parse(answer.result.freshUntil),
          });
        } catch {
          found.set(stop.stopId, {
            kind: "unavailable",
            because: "No time to this stop: GEO-04 did not answer",
          });
        }
        if (!current) return;
      }
      setLegs(found);
      setLegsRead(true);
    })();
    return (): void => {
      current = false;
    };
  }, [api, plan, places, placesRead]);

  /*
   * The clock, once a second, and only while a drawn duration is still fresh.
   *
   * It exists to notice an answer **expiring**, which is the one thing on this screen that changes
   * with neither a rider acting nor a service being asked again. It stops once every leg has gone
   * stale, because a timer running against nothing is a timer nobody can see stop.
   */
  useEffect(() => {
    const fresh = [...legs.values()].some(
      (leg) => leg.kind === "duration" && leg.freshUntil > now(),
    );
    if (!fresh) return;
    const timer = setInterval(() => setTick(now()), 1000);
    return (): void => clearInterval(timer);
  }, [legs, now]);

  const raiseSafety = useCallback(
    (what: string, command: Parameters<RiderApi["raiseSafetyIncident"]>[0]["command"]) => {
      const tripId = plan?.tripId;
      if (tripId === undefined) return;
      setRefused(undefined);
      void api
        .raiseSafetyIncident({ tenantId, tripId, messageId: props.newId(), command })
        .catch((error: unknown) => setRefused({ what, why: reasonOf(error) }));
    },
    [api, tenantId, plan, props],
  );

  if (planId === undefined) {
    return (
      <MultiStopState
        state={{
          kind: "error",
          code: "no_plan_in_address",
          message:
            "This screen is about one stop plan and the address does not name one. TRP-04 publishes a read by planId and no listing, so nothing can find it.",
          retryable: false,
          conflict: false,
        }}
        onBack={props.onBack}
      />
    );
  }

  if (state.kind !== "ready" || plan === undefined) {
    return <MultiStopState state={state} onRetry={load} onBack={props.onBack} />;
  }

  const ordered = inSequence(plan.stops);
  /*
   * The drop-off is the last stop, and the ones before it are what the chip counts.
   *
   * `13 WP12`'s own two multi-stop screens are the evidence rather than a convention. `Multi-stop`
   * draws `Pickup`, `Stop 1`, `Stop 2` and `Drop-off` under a bar reading `Trip with 3 stops`; this
   * screen draws three rows under `In trip · 3 stops` with a chip reading `stop 1 of 2 done` and a
   * sub-line reading `Stop 2 of 2, then JKIA`. So the bar counts every row of the plan, the chip and
   * the sub-line count the rows before the last, and `location_on` is the glyph both screens give
   * that last row.
   */
  const intermediate = ordered.slice(0, Math.max(ordered.length - 1, 0));
  const done = intermediate.filter((stop) => stop.status === "completed").length;
  const ahead = ordered.filter(stillToCome);
  const next = ahead[0];
  const last = ordered[ordered.length - 1];
  const nextPlace = next === undefined ? undefined : places.get(next.addressId);
  const headline = headlineFor({ ordered, next, nextPlace, placeFailures, placesRead });

  /*
   * Where the words on this screen are this build's rather than the design's.
   *
   * The design draws one state: a plan mid-trip whose next stop is an intermediate one, none of
   * whose stops was skipped, and no command of whose was refused. Each of the five below is a state
   * this screen reaches and the design is silent about, so it is said under the sub-line rather than
   * passing as approved copy — and none is reached in the state the acceptance check compares.
   */
  const flags = [
    headline.flag,
    next !== undefined && !intermediate.some((stop) => stop.stopId === next.stopId)
      ? "`Last stop` is this build's wording where the design writes `Stop 2 of 2, then JKIA`"
      : undefined,
    ordered.some((stop) => stop.status === "skipped")
      ? "`skipped` is this build's word for a stop TRP-04 marked skipped"
      : undefined,
    refused === undefined ? undefined : "the refusal card's wording is this build's",
  ].filter((flag): flag is string => flag !== undefined);

  const rows: RowValue[] = ordered.map((stop, index) => {
    const place = places.get(stop.addressId);
    const isLast = index === ordered.length - 1;
    return {
      id: stop.stopId,
      lead: leadGlyph(stop, { isLast, position: stop.sequence }),
      ...(stop.status === "completed" && !isLast ? { leadIsDone: true } : {}),
      ...nameFor(stop, place, placeFailures, placesRead),
      ...subFor(stop, legs.get(stop.stopId), { at: tick, read: legsRead }),
      badge: badgeFor(stop, { isNext: next !== undefined && stop.stopId === next.stopId }),
      ...(stop.status === "skipped" ? { dimmed: true } : {}),
    };
  });

  const armed = props.scopes.has(SCOPES.safety) && plan.tripId.length > 0;
  const pending = !placesRead || !legsRead;

  return (
    <MarketShell
      /*
       * The second class is the acceptance check's handle on "this screen has finished reading".
       *
       * It styles nothing. The check waits for a selector before photographing, and every selector
       * this shell offers exists from the first paint — so without a marker the capture would be of
       * a screen whose places were still being fetched, which is a correct build measured against
       * the design as a difference.
       */
      screen={`multi-stop-in-trip${pending ? " multi-stop-reading" : ""}`}
      left={{ glyph: "arrow_back", label: "Back", onPress: props.onBack }}
      barTitle={`In trip · ${String(ordered.length)} ${ordered.length === 1 ? "stop" : "stops"}`}
      barSub={
        <>
          {`${TENANT_NAME} · `}
          <Absent words="No class: TRP-04's plan publishes no product, class or vehicle" />
        </>
      }
      right={{
        glyph: "sos",
        label: "Emergency help",
        ...(armed
          ? { onPress: (): void => raiseSafety("Emergency help", { type: "open_sos" }) }
          : { unavailable: NO_SAFETY_SCOPE }),
      }}
      rightIsSos
      badge={{
        kind: next === undefined && ordered.length > 0 ? "done" : "active",
        label: `stop ${String(done)} of ${String(intermediate.length)} done`,
      }}
      title={headline.title}
      {...(headline.titleAbsent === undefined ? {} : { titleAbsent: headline.titleAbsent })}
      sub={subLine({
        next,
        intermediate,
        lastPlace: last === undefined ? undefined : places.get(last.addressId),
      })}
      {...(flags.length === 0 ? {} : { flag: flags.join(" · ") })}
      actions={[
        {
          label: "Share trip",
          glyph: "ios_share",
          primary: false,
          ...(armed
            ? {
                onPress: (): void =>
                  raiseSafety("Share trip", { type: "set_sharing", enabled: true }),
              }
            : { unavailable: NO_SAFETY_SCOPE }),
        },
      ]}
    >
      <Group
        rows={rows}
        empty={
          <EmptyRow words="No stops: TRP-04 answered a plan with none, and a trip with no stop has nothing to head for" />
        }
      />
      {refused === undefined ? null : (
        <ConflictCard headline={`${refused.what} was not accepted`} detail={refused.why} />
      )}
    </MarketShell>
  );
}

/** The screen's own loading, offline, forbidden and error states, in the shell's own vocabulary. */
function MultiStopState(props: {
  readonly state: ViewState;
  readonly onRetry?: () => void;
  readonly onBack: () => void;
}): ReactNode {
  return (
    <MarketState
      state={props.state}
      {...(props.onRetry === undefined ? {} : { onRetry: props.onRetry })}
      onBack={props.onBack}
      purpose="Reading the stops on this trip."
      barTitle="In trip"
      barSub={TENANT_NAME}
    />
  );
}

/** A safety command SAF-01 did not accept, and what it was. */
interface Refusal {
  readonly what: string;
  readonly why: string;
}

/**
 * `Heading to Upper Hill` — the headline, which is the whole of what this screen was blocked on.
 *
 * Three states, and only the first is drawn in the design. A plan whose every stop is behind the
 * trip has nowhere to head for, and a next stop whose address did not resolve has no name — and the
 * second of those must not become a plausible place name, which is the one thing a dashed pill is
 * for.
 */
function headlineFor(input: {
  readonly ordered: readonly StopPlanStop[];
  readonly next: StopPlanStop | undefined;
  readonly nextPlace: AddressView | undefined;
  readonly placeFailures: ReadonlyMap<string, string>;
  readonly placesRead: boolean;
}): { readonly title: string; readonly titleAbsent?: string; readonly flag?: string } {
  if (input.ordered.length === 0) {
    return {
      title: "This plan has no stops",
      flag: "`This plan has no stops` is this build's wording",
    };
  }
  if (input.next === undefined) {
    return { title: "Every stop is done", flag: "`Every stop is done` is this build's wording" };
  }
  if (input.nextPlace === undefined) {
    return {
      title: "Heading to the next stop",
      titleAbsent: input.placesRead
        ? (input.placeFailures.get(input.next.addressId) ??
          "No place name: GEO-02 did not answer for this stop's address")
        : "No place name yet: this stop's address has not been read",
    };
  }
  return { title: `Heading to ${input.nextPlace.formattedAddress}` };
}

/**
 * The card's second line: `Stop 2 of 2, then JKIA · arrive about 13:40`, less the time.
 *
 * Three facts in one sentence and the third has no source. The first two are the next stop's number
 * among the stops before the drop-off and the drop-off's own name. The arrival estimate is the legs
 * plus the dwell at each stop, and the dwell is unpublished — so it is drawn as the absence in its
 * own place rather than computed from the legs alone, which would be a time a rider acts on and
 * arrives after.
 */
function subLine(input: {
  readonly next: StopPlanStop | undefined;
  readonly intermediate: readonly StopPlanStop[];
  readonly lastPlace: AddressView | undefined;
}): ReactNode {
  const arrival =
    (
      /*
       * Short, because it stands in a 13px sub-line the design fills with `13:40`.
       *
       * The whole reason — that an arrival over the remaining stops is the legs plus the dwell at each
       * one, so summing the legs alone draws a time a rider arrives after — is in this file's header,
       * in `navigation.ts`'s `absent` entry and in the design question. What the pill has to carry is
       * the two field names, which is what makes it checkable.
       */
      <Absent words="No arrival time: nothing publishes plan.stops.waitSeconds or plan.stops.dwellSeconds" />
    );
  if (input.next === undefined) {
    return (
      <>
        {"All stops done · arrive about "}
        {arrival}
      </>
    );
  }

  const among = input.intermediate.findIndex((stop) => stop.stopId === input.next?.stopId);
  const where =
    among === -1
      ? "Last stop"
      : `Stop ${String(among + 1)} of ${String(input.intermediate.length)}`;
  // `then JKIA` names the drop-off, so it is said only while the drop-off is not the next stop.
  const then = among === -1 ? undefined : input.lastPlace?.formattedAddress;

  return (
    <>
      {`${where}${then === undefined ? "" : `, then ${then}`} · arrive about `}
      {arrival}
    </>
  );
}

/** A stop the trip has not finished with: `pending` or `arrived`, and never `skipped`. */
function stillToCome(stop: StopPlanStop): boolean {
  return stop.status === "pending" || stop.status === "arrived";
}

/** The row's own name, which is a GEO-02 address or the reason there is none. */
function nameFor(
  stop: StopPlanStop,
  place: AddressView | undefined,
  failures: ReadonlyMap<string, string>,
  read: boolean,
): { readonly title: string } | { readonly title: string; readonly titleAbsent: string } {
  if (place !== undefined) return { title: place.formattedAddress };
  return {
    title: "This stop",
    titleAbsent: read
      ? (failures.get(stop.addressId) ??
        "No place name: GEO-02 did not answer for this stop's address")
      : "No place name yet: this stop's address has not been read",
  };
}

/**
 * The row's own sub-line, which is a different fact depending on where the stop is.
 *
 * `Arrived 12:58 · left 13:02` is the design's own sentence for a stop behind the trip and
 * `About 9 min` its own for one ahead of it. Both halves of the first are nullable in the contract,
 * and there is no `departedAt` at all: a stop's `completedAt` is the instant the design calls
 * "left", which is a reading of two vocabularies rather than a published mapping and is recorded as
 * a design question.
 */
function subFor(
  stop: StopPlanStop,
  leg: Leg | undefined,
  clockState: { readonly at: number; readonly read: boolean },
): { readonly sub: string } | { readonly sub: string; readonly subAbsent: string } {
  if (stop.status === "skipped") {
    return { sub: stop.skipReason === null ? "Skipped" : `Skipped · ${stop.skipReason}` };
  }
  if (stop.status === "completed" || stop.status === "arrived") {
    const arrived = stop.arrivedAt === null ? undefined : clock(stop.arrivedAt);
    const left = stop.completedAt === null ? undefined : clock(stop.completedAt);
    if (arrived !== undefined && left !== undefined) {
      return { sub: `Arrived ${arrived} · left ${left}` };
    }
    if (arrived !== undefined) return { sub: `Arrived ${arrived}` };
    if (left !== undefined) return { sub: `Left ${left}` };
    return { sub: "", subAbsent: "TRP-04 published no arrivedAt or completedAt for this stop" };
  }
  if (leg === undefined) {
    return {
      sub: "",
      subAbsent: clockState.read
        ? "No time to this stop: GEO-04 answered for no leg into it"
        : "No time to this stop: GEO-04 has not been asked yet",
    };
  }
  if (leg.kind === "unavailable") return { sub: "", subAbsent: leg.because };
  /*
   * GEO-04's own freshness, which is why this is not a number drawn once and left there.
   *
   * `result.freshUntil` is published beside the duration and this screen does not re-ask. So the
   * duration stands while GEO-04 says it is fresh and becomes the absence the moment it does not: a
   * stale minute count on a screen somebody is navigating by is worse than no count, which is R-23's
   * finding about a position drawn as current.
   */
  if (Number.isFinite(leg.freshUntil) && clockState.at > leg.freshUntil) {
    return {
      sub: "",
      subAbsent: `No time to this stop: GEO-04's answer stopped being fresh at ${clock(new Date(leg.freshUntil).toISOString())}`,
    };
  }
  return { sub: `About ${String(minutes(leg.seconds))} min` };
}

/**
 * The lead glyph, from the design's own set.
 *
 * `location_on` for the last row on both multi-stop screens — design rule 5's pin for a trip
 * destination — `check_circle` for a stop behind the trip, and `counter_<n>` for one ahead of it. The
 * design draws `counter_1` and `counter_2`, and Material Symbols Rounded runs out at `counter_9`, so
 * a tenth stop onwards draws `more_horiz`, which is a glyph the same file uses elsewhere. That last
 * choice is this build's and is recorded, because the design has no row for it: ADR 0036 says a plan
 * may carry twenty stops.
 */
function leadGlyph(
  stop: StopPlanStop,
  where: { readonly isLast: boolean; readonly position: number },
): string {
  if (where.isLast) return "location_on";
  if (stop.status === "completed") return "check_circle";
  return where.position >= 1 && where.position <= 9
    ? `counter_${String(where.position)}`
    : "more_horiz";
}

/**
 * The row's badge: a shape and a word, never a colour alone.
 *
 * `completed`, `next` and `later` are the design's own three words. `skipped` and `arrived` are
 * TRP-04's own status values, drawn where the design has no word — `skipped` on the `closed` shape
 * the design gives a row that cannot be acted on, and flagged on the card as this build's word.
 */
function badgeFor(stop: StopPlanStop, where: { readonly isNext: boolean }): BadgeValue {
  if (stop.status === "skipped") return { kind: "closed", label: "skipped" };
  if (stop.status === "completed") return { kind: "done", label: "completed" };
  if (where.isNext)
    return { kind: "active", label: stop.status === "arrived" ? "arrived" : "next" };
  return { kind: "idle", label: "later" };
}

/** The stops in the order the plan puts them in, which is `sequence` and not array order. */
function inSequence(stops: readonly StopPlanStop[]): readonly StopPlanStop[] {
  return [...stops].sort((one, other) => one.sequence - other.sequence);
}

/** The last stop before this one the trip actually finished, which is where a leg starts from. */
function lastCompleted(
  ordered: readonly StopPlanStop[],
  before: StopPlanStop,
): StopPlanStop | undefined {
  let found: StopPlanStop | undefined;
  for (const stop of ordered) {
    if (stop.sequence >= before.sequence) break;
    if (stop.status === "completed") found = stop;
  }
  return found;
}

/**
 * `About 9 min`, which **ceils**.
 *
 * The same direction Q-126 records for `driver · 4 To pickup` and the opposite of a floor: a leg of
 * 8 minutes 10 seconds drawn as `About 8 min` tells a rider they arrive before they will.
 */
function minutes(seconds: number): number {
  return Math.max(1, Math.ceil(seconds / 60));
}

/** `12:58`, on this device's clock, which is the only clock a phone has. */
function clock(at: string): string {
  return new Date(at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

/** What a refused safety command is said to have been refused for. */
function reasonOf(error: unknown): string {
  const state = stateForError(error, [SCOPES.safety]);
  if (state.kind === "offline") return "This phone could not reach the platform.";
  if (state.kind === "forbidden") return NO_SAFETY_SCOPE;
  if (state.kind === "error") return state.message;
  return "SAF-01 did not accept it.";
}
